import React, { Component } from 'react'
import PropTypes from 'prop-types'

import * as autosize from 'autosize'
import * as getLineHeight from 'line-height'

import Like from '@components/Like'
import LoginAlert from '@components/Login/LoginAlert'

import { compose, withTranslation, getContext } from '@hocs'
import { imageFileUploadCheck, convertFilesToList } from './imageFileUploadCheck'

import FavoriteButtonContainer from '@components/FavoriteButton'

import cx from 'classnames'
import { StyledBottomBar } from './index.styled'
import { trackArticleBottomAnchorSaveEvent } from '@libs/eventTracking/article/trackArticleBottomAnchorEvent'
import {
  dispatchReplyCloseEvent,
  dispatchReplyFinishEvent,
  dispatchReplyOpenEvent,
} from '@libs/customEvents/reply'

export const BottomBarType = Object.freeze({
  Reply: 'REPLY',
  Choice: 'CHOICE',
  Ghost: 'GHOST',
})

class BottomBar extends Component {
  static defaultProps = {
    bottomBarType: BottomBarType.Reply,
    replyBarIsOpen: false,
    loginAlertIsOpen: false,
    likeNum: 0,
    replyNum: 0,
    savedNum: 0,
    replyTarget: '',
    via: '',
  }

  constructor(props) {
    super(props)
    this._refs = {}
    const { isLike = false } = props.post.toJS()
    this.state = {
      isLike,
      likeNum: props.likeNum,
      maxRows: 5,
      lineHeight: null,
    }
  }

  handleRef = (name) => (node) => (this._refs[`${name}`] = this._refs[`${name}`] || node)

  componentDidMount() {
    this.updateLineHeight()
    this.autoSizeReply()
  }

  componentDidUpdate(prevProps) {
    if (this.props.replyBarIsOpen) this._refs.reply.focus()
    if (prevProps.post.get('isLike') !== this.props.post.get('isLike')) {
      this.setState({ isLike: this.props.post.get('isLike') })
    }
    if (prevProps.likeNum !== this.props.likeNum) {
      this.setState({ likeNum: this.props.likeNum })
    }
  }

  componentWillUnmount() {
    this.autoSizeReplyDestroy()
  }

  autoSizeReply = () => this._refs.reply && autosize(this._refs.reply) // register autosize

  autoSizeReplyDestroy = () => this._refs.reply && autosize.destroy(this._refs.reply) // destroy autosize

  updateLineHeight = () =>
    this._refs.reply &&
    this.setState((state) => ({ ...state, lineHeight: getLineHeight(this._refs.reply) }))

  openCommentPop = () => this.props.openComment()

  openReplyBar = () => {
    window.clearTimeout(window.replyTimer)
    this.props.setReplyId?.()
    dispatchReplyOpenEvent({ component: 'bottom-anchor' })
  }

  closeReplyBar = (delay = 0) => {
    window.replyTimer = window.setTimeout(() => this.props.closeReplyBar(), delay)
    dispatchReplyCloseEvent({ component: 'bottom-anchor' })
  }

  sendReply = async () => {
    const { postId: postID, postReply } = this.props
    if (postReply && this._refs.reply.value.trim() !== '') {
      const result = await postReply({ postID, text: this._refs.reply.value || '' })
      dispatchReplyFinishEvent(result)
    }
    this._refs.reply.value = ''
    this.closeReplyBar()
  }

  _handleOnClickLike = () => {
    const { postId: postID, postLike } = this.props
    const isLike = !this.state.isLike
    const likeNum = this.state.likeNum + (!this.state.isLike ? 1 : -1)
    this.setState(
      (state) => ({ ...state, isLike, likeNum }),
      () => {
        if (postLike) {
          postLike({ postID, isLike: isLike ? 1 : 0 })
        }
      },
    )
  }

  _handleFileUpload = async (e) => {
    const { postId: postID, postReply, t } = this.props
    const fileInput = e.target || this._refs.imageUpload
    const errors = imageFileUploadCheck(fileInput)

    if (errors.length === 0) {
      const imgList = await convertFilesToList(fileInput.files)
      if (postReply && imgList.length && this._refs.imageUpload.value.trim() !== '') {
        postReply({ postID, imgList })
      }
    } else {
      const error = errors[0]
      const { type, ...etc } = error
      alert(t(`${type}`, { ...etc }))
    }

    this._refs.imageUpload.value = ''
    this.closeReplyBar()
  }

  _handleArticleClickFavorite = (topics) => {
    const postData = this.props.post.toJS()
    topics.forEach(({ postID: topicId, d: actionTypeEnum }) => {
      const actionType = actionTypeEnum ? 'unsave' : 'save'
      trackArticleBottomAnchorSaveEvent(postData, actionType, topicId)
    })
  }

  render() {
    const { lineHeight = null, isLike = false, likeNum = 0, maxRows } = this.state
    const {
      loginAlertIsOpen,
      closeLoginAlert,
      replyBarIsOpen,
      replyNum,
      savedNum,
      t,
      postId,
      post,
      bottomBarType,
      replyTarget,
      via,
    } = this.props
    const target = replyTarget ? replyTarget : t('post:內容')
    const maxHeight = lineHeight ? lineHeight * maxRows : null
    const isReplyType = bottomBarType === BottomBarType.Reply
    const classNames = cx('bottomBar', { ['bottomBarReply']: isReplyType })
    const replyClassNames = cx('bottomBar__cols', 'bottomBar__cols--3', {
      ['bottomBar__post']: isReplyType,
    })

    return (
      <StyledBottomBar>
        <div className={classNames}>
          <LoginAlert target="_self" isOpen={loginAlertIsOpen} onClose={closeLoginAlert} />
          {bottomBarType === BottomBarType.Choice && (
            <ul className="bottomBar__cols bottomBar__cols--2">
              <li className="bottomBar__col" onClick={this.openCommentPop}>
                <span className="bottomBar__icon icon icon--reply" />
                <span className="bottomBar__text">{t('寫評論')}</span>
                <span className="bottomBar__num">{replyNum}</span>
              </li>
              <li className="bottomBar__col">
                <FavoriteButtonContainer
                  savedNum={savedNum}
                  postId={postId}
                  postItem={post}
                  isAddToDefault={true}
                  via={via}
                />
              </li>
            </ul>
          )}
          {bottomBarType === BottomBarType.Ghost && (
            <ul className="bottomBar__cols bottomBar__cols--2">
              <li className="bottomBar__col">
                <FavoriteButtonContainer
                  savedNum={savedNum}
                  postId={postId}
                  postItem={post}
                  isAddToDefault={true}
                  via={via}
                />
              </li>
            </ul>
          )}
          {bottomBarType === BottomBarType.Reply && (
            <ul className={replyClassNames} style={{ display: replyBarIsOpen ? 'none' : 'flex' }}>
              <li className="bottomBar__col">
                <Like
                  postBottom
                  id={postId}
                  likeNum={likeNum}
                  isLike={!!isLike}
                  onClickLike={this._handleOnClickLike}
                />
              </li>
              <li className="bottomBar__col" onClick={this.openReplyBar}>
                <span className="bottomBar__icon icon icon--reply" />
                <span className="bottomBar__text">{t('post:回覆')}</span>
                <span className="bottomBar__num">{replyNum}</span>
              </li>
              <li className="bottomBar__col">
                <FavoriteButtonContainer
                  savedNum={savedNum}
                  postId={postId}
                  postItem={post}
                  isAddToDefault={false}
                  via={via}
                  onChange={this._handleArticleClickFavorite}
                />
              </li>
            </ul>
          )}
          <div
            className="replyBar bottomBar__replyBar"
            style={{ display: replyBarIsOpen ? 'flex' : 'none' }}
          >
            <div className="replyBar__textareaWrapper">
              <textarea
                ref={this.handleRef('reply')}
                className="replyBar__textarea"
                placeholder={t('post:回覆內容', { target })}
                onBlur={() => this.closeReplyBar(300)}
                style={{ maxHeight }}
              />
              <label
                htmlFor="imageUpload"
                className="replyBar__uploadImg"
                onClick={() => this.closeReplyBar(0)}
              />
              <input
                ref={this.handleRef('imageUpload')}
                id="imageUpload"
                type="file"
                accept="image/x-png,image/gif,image/jpeg"
                onChange={this._handleFileUpload}
              />
            </div>
            <span className="replyBar__send" onClick={this.sendReply}>
              {t('post:回覆')}
            </span>
          </div>
        </div>
      </StyledBottomBar>
    )
  }
}

export default compose(
  withTranslation(),
  getContext({
    postLike: PropTypes.func,
    postReply: PropTypes.func,
    setReplyId: PropTypes.func,
    closeReplyBar: PropTypes.func,
  }),
)(BottomBar)
